// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
// ── Platform surface ids and the boundary tag ──────────────────────────────
export const PCF_SUPERVISION_REGISTER_SURFACES = {
    /** Register (a) — information a recipient received through sharing. */
    sharedInformation: 'pcf_supervision_shared_information',
    /** Register (b) — publicity carried out on a branch requirement, per channel. */
    publicity: 'pcf_supervision_branch_publicity',
    /** Register (c) — information provided on a branch request. */
    informationRequest: 'pcf_supervision_branch_information_request',
    /** Register (d) — depositor enquiries, routed to the branch only. */
    depositorEnquiry: 'pcf_supervision_depositor_enquiry',
    /** AC12 — reporting calendar from the list the organisation holds. */
    reportingCalendar: 'pcf_supervision_reporting_calendar',
    /** AC13 — information-system incident and resumption. */
    incident: 'pcf_supervision_incident_resumption',
    /** AC14 — error or query notice, resend and unlock request. */
    correction: 'pcf_supervision_correction_resend_unlock',
    /** AC15 — the dates the organisation was told the system operates / officially operates. */
    systemOperation: 'pcf_supervision_system_operation',
    /** AC16 — connection standard, special control, database and readiness. */
    connectionReadiness: 'pcf_supervision_connection_readiness',
    /**
     * Sprint 62.50.1 — register (a): a recipient's written request for information
     * beyond its sharing scope, and the answer when one is recorded. Shown on the
     * Shared Supervisory Information page; never a register page of its own.
     */
    writtenRequest: 'pcf_supervision_written_request',
    /** Sprint 62.50.1 — register (a): a recipient's proposal to add, amend or abolish shared content. */
    changeProposal: 'pcf_supervision_change_proposal',
};
/**
 * The collections the registers persist to. Platform names, one per record
 * family; `system_operation` and `connection_readiness` hold one record per
 * organisation.
 */
export const PCF_BRANCH_REQUIREMENTS_COLLECTION = 'grc_pcf_branch_requirements';
export const PCF_PUBLICITY_RECORDS_COLLECTION = 'grc_pcf_publicity_records';
export const PCF_INFORMATION_REQUESTS_COLLECTION = 'grc_pcf_information_requests';
export const PCF_DEPOSITOR_ENQUIRIES_COLLECTION = 'grc_pcf_depositor_enquiries';
export const PCF_SHARED_INFORMATION_ENTRIES_COLLECTION = 'grc_pcf_shared_information_entries';
export const PCF_REPORTING_CALENDAR_ENTRIES_COLLECTION = 'grc_pcf_reporting_calendar_entries';
export const PCF_INCIDENT_RECORDS_COLLECTION = 'grc_pcf_incident_records';
export const PCF_CORRECTION_RECORDS_COLLECTION = 'grc_pcf_correction_records';
export const PCF_SYSTEM_OPERATION_RECORDS_COLLECTION = 'grc_pcf_system_operation_records';
export const PCF_CONNECTION_READINESS_RECORDS_COLLECTION = 'grc_pcf_connection_readiness_records';
/** Sprint 62.50.1 — register (a)'s written requests and proposals. */
export const PCF_WRITTEN_REQUESTS_COLLECTION = 'grc_pcf_written_requests';
export const PCF_CHANGE_PROPOSALS_COLLECTION = 'grc_pcf_change_proposals';
/** The same names, keyed by record family. */
export const PCF_SUPERVISION_COLLECTIONS = {
    branchRequirements: PCF_BRANCH_REQUIREMENTS_COLLECTION,
    publicity: PCF_PUBLICITY_RECORDS_COLLECTION,
    informationRequests: PCF_INFORMATION_REQUESTS_COLLECTION,
    depositorEnquiries: PCF_DEPOSITOR_ENQUIRIES_COLLECTION,
    sharedInformation: PCF_SHARED_INFORMATION_ENTRIES_COLLECTION,
    reportingCalendar: PCF_REPORTING_CALENDAR_ENTRIES_COLLECTION,
    incidents: PCF_INCIDENT_RECORDS_COLLECTION,
    corrections: PCF_CORRECTION_RECORDS_COLLECTION,
    systemOperation: PCF_SYSTEM_OPERATION_RECORDS_COLLECTION,
    connectionReadiness: PCF_CONNECTION_READINESS_RECORDS_COLLECTION,
    writtenRequests: PCF_WRITTEN_REQUESTS_COLLECTION,
    changeProposals: PCF_CHANGE_PROPOSALS_COLLECTION,
};
/**
 * Sprint 62.50.1 — the nine register surfaces an export is taken of
 * (`GET /export/:surfaceKey`), in the order the registers list them. The two
 * register (a) records are not surfaces of their own here: the
 * `sharedInformation` export prints them as two further tables.
 */
export const PCF_SUPERVISION_EXPORT_SURFACE_KEYS = [
    'sharedInformation',
    'publicity',
    'informationRequest',
    'depositorEnquiry',
    'reportingCalendar',
    'incident',
    'correction',
    'systemOperation',
    'connectionReadiness',
];
/** The locales an export is written in (D3): the pack's own EN and VI. */
export const PCF_SUPERVISION_EXPORT_LOCALES = ['en', 'vi'];
/**
 * Sprint 62.50.1 — the most records one export carries, across all its tables
 * (422 `PCF_EXPORT_TOO_LARGE` above it). 25 reads of the register's own page
 * size; well past any one organisation's register of this kind, and inside what
 * the PDF and DOCX builders render in one request.
 */
export const PCF_SUPERVISION_EXPORT_MAX_RECORDS = 5000;
/** The one guidance tag every page of these registers renders through `BoundarySection`. */
export const PCF_SUPERVISION_BOUNDARY_TAG = 'pcf_supervision_boundary';
/**
 * Pack attribute names the registers read. Platform vocabulary only: each is a
 * KEY a pack may author, never a value from any instrument.
 */
export const PCF_PACK_ATTR = {
    /** Template- or section-level: the requirement ids whose applicability decides whether the duty reaches the organisation. */
    appliesToRequirementIds: 'applies_to_requirement_ids',
    /** Option-level (scope limb): this limb needs an approval reference. */
    requiresApprovalReference: 'requires_approval_reference',
    /** Option-level (channel): this channel may be recorded as an absence statement ("the fund has no website"). */
    absenceStatementAllowed: 'absence_statement_allowed',
    /** Option-level (channel): this channel is support the branch asked for, never the organisation's own duty. */
    branchRequestedSupport: 'branch_requested_support',
    /** Option-level (notice type): the installed deadline record whose clock runs from the notice. */
    clockDeadlineId: 'clock_deadline_id',
    /** Tracked follow-on item: the register surfaces it holds as not started until it is issued. */
    holdsRegisterSurfaces: 'holds_register_surfaces',
    /** Tracked follow-on item, only when `issued`: the start the issued text states (calendar date). */
    heldActivityStartsOn: 'held_activity_starts_on',
    /** Tracked follow-on item, only when `issued`: the handling period the issued text states, as words. */
    heldActivityHandlingPeriod: 'held_activity_handling_period',
    /** Option-level (scope limb): the values of the same template's `recipient` field this limb belongs to. */
    recipientValues: 'recipient_values',
};
/** The deadline-record key that rolls a deadline to the next working day. */
export const PCF_NEXT_WORKING_DAY_RULE = 'next_working_day';
// ── The branch requirement (header of registers (b) and (c)) ───────────────
/** What a branch requirement asks for. Platform vocabulary. */
export const PCF_REQUIREMENT_KINDS = ['publicity', 'information'];
/**
 * `details.reason` on a 422 `PCF_REQUIREMENT_KIND_MISMATCH` (fix sprint #683
 * gate E12.5 pass 1). One code answers three refusals; the reason tells them
 * apart so a page can say the sentence that is true for the one it met.
 */
export const PCF_KIND_MISMATCH_REASONS = {
    /** A create names a requirement that does not ask for the record's kind (field `requirement_id`). */
    kindNotAsked: 'kind_not_asked',
    /** An edit removes a kind while a record of it exists or a create's claim on it is in flight (field `asks_for`). */
    recordsHeldOrInFlight: 'records_held_or_in_flight',
    /** An edit stops asking for publicity while a publicity completion is recorded and not cleared (field `asks_for`). */
    publicityCompletionRecorded: 'publicity_completion_recorded',
};
/**
 * `details.reason` on a 422 `PCF_DATE_ORDER_INVALID` (fix sprint #683 gate
 * E12.5 pass 1), beside `details.field`. Every refusal names one reason.
 */
export const PCF_DATE_ORDER_REASONS = {
    /** A publicity `published_on` or a request's `provided_on` is before the requirement's received date. */
    beforeRequirementReceived: 'before_requirement_received',
    /** A received date moved after a publicity record's `published_on` under the requirement. */
    receivedAfterPublication: 'received_after_publication',
    /** A received date moved after an information request's `provided_on` under the requirement. */
    receivedAfterProvision: 'received_after_provision',
    /** A received date moved after the stored publicity completion. */
    receivedAfterCompletion: 'received_after_completion',
    /** A publicity completion dated before the requirement was received. */
    completionBeforeReceived: 'completion_before_received',
    /** An incident's `fixed_at` before it started. */
    fixedBeforeStart: 'fixed_before_start',
    /** Sprint 62.50.1 — a written request's `answered_on` before its `sent_on`. */
    answeredBeforeSent: 'answered_before_sent',
};
export const PCF_SUPERVISION_ERROR_CODES = {
    requirementNotFound: 'PCF_BRANCH_REQUIREMENT_NOT_FOUND',
    recordNotFound: 'PCF_RECORD_NOT_FOUND',
    optionNotDeclared: 'PCF_OPTION_NOT_DECLARED',
    noDeclaringFramework: 'PCF_REGISTER_NOT_DECLARED',
    approvalReferenceRequired: 'PCF_APPROVAL_REFERENCE_REQUIRED',
    personalDataMechanismRequired: 'PCF_PERSONAL_DATA_MECHANISM_REQUIRED',
    absenceStatementNotAllowed: 'PCF_ABSENCE_STATEMENT_NOT_ALLOWED',
    channelFieldsRequired: 'PCF_CHANNEL_FIELDS_REQUIRED',
    informationRequestNotFound: 'PCF_INFORMATION_REQUEST_NOT_FOUND',
    versionConflict: 'PCF_RECORD_VERSION_CONFLICT',
    attachmentNotOwned: 'PCF_ATTACHMENT_NOT_OWNED',
    userNotFound: 'PCF_USER_NOT_FOUND',
    sectionNotOffered: 'PCF_SECTION_NOT_OFFERED',
    resumedBeforeFix: 'PCF_RESUMED_BEFORE_FIX',
    scopeLimbRecipientMismatch: 'PCF_SCOPE_LIMB_RECIPIENT_MISMATCH',
    requirementKindMismatch: 'PCF_REQUIREMENT_KIND_MISMATCH',
    resentBeforeNotice: 'PCF_RESENT_BEFORE_NOTICE',
    optionNotOffered: 'PCF_OPTION_NOT_OFFERED',
    surfaceUnknown: 'PCF_SURFACE_UNKNOWN',
    dateOrderInvalid: 'PCF_DATE_ORDER_INVALID',
    /**
     * 422: an unlock decision or an unlock request recorded on a report that is
     * not locked. Carries `details.field` and `field_errors` like
     * `PCF_DATE_ORDER_INVALID`; it is a state rule, not a date order.
     */
    unlockWithoutLock: 'PCF_UNLOCK_WITHOUT_LOCK',
    /**
     * 403 (fix sprint #683 Finding 1): a branch-requirement header edit needs the
     * write grant of the register(s) the requirement heads, read off what it asks
     * for as stored. `details.required_groups` lists every grant group the edit
     * needs, each satisfied by any one of its grants: the kinds the requirement
     * asks for now; and, when the edit changes `asks_for`, the kinds it will ask
     * for after it and each kind added or removed on its own. `details.missing_groups`
     * is the groups the caller does not satisfy.
     */
    requirementGrantRequired: 'PCF_REQUIREMENT_GRANT_REQUIRED',
    /**
     * 404 (Sprint 62.50.1): a written request or proposal names a register (a)
     * entry that is not one of this organisation's. `details.entry_ids` lists them.
     */
    relatedEntryNotFound: 'PCF_RELATED_ENTRY_NOT_FOUND',
    /**
     * 422 (Sprint 62.50.1): a written request's answer, its reference and its date
     * are recorded together or not at all. `details.field_errors` names each one missing.
     */
    answerFieldsRequired: 'PCF_ANSWER_FIELDS_REQUIRED',
    /**
     * 422 (Sprint 62.50.1): an export would carry more than
     * `PCF_SUPERVISION_EXPORT_MAX_RECORDS` records. `details.record_count` and
     * `details.max_records` say by how much.
     */
    exportTooLarge: 'PCF_EXPORT_TOO_LARGE',
};
//# sourceMappingURL=pcf-supervision.js.map